import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Users, Shield, Clock, ArrowRight, Star, MapPin } from 'lucide-react';

export default function Home() {
  const services = [
    {
      title: 'Furnace Install & Replacement',
      description: 'High-efficiency natural gas, propane, and oil furnace replacement. Right-sized for your home.',
      link: '/services#furnace'
    },
    {
      title: 'AC & Heat Pump Install',
      description: 'Cooling-only central AC or cold-climate heat pumps that qualify for rebates.',
      link: '/services#ac-heat-pump'
    },
    {
      title: 'Service & Maintenance',
      description: 'Same-week service, same-day for emergencies. Annual maintenance from $189/year.',
      link: '/services#maintenance'
    },
    {
      title: 'Indoor Air Quality',
      description: 'HEPA filtration, HRVs, humidifiers, UV treatment for healthier home air.',
      link: '/services#air-quality'
    }
  ];

  const testimonials = [
    {
      text: "Our furnace died on the coldest weekend of January. Northbridge had a tech at the house Saturday afternoon, the new unit installed Monday morning, and the price matched the quote. The tech (Marco) wore booties and put a drop cloth across our hallway runner. Small thing. Mattered.",
      author: "Janet R.",
      location: "Maple"
    },
    {
      text: "We had three quotes for a heat pump conversion. Northbridge was middle on price but they were the only ones who actually measured the ductwork before quoting. The other two just looked at the existing furnace tonnage and guessed. We went with Northbridge and the system is sized right.",
      author: "Devon T.",
      location: "Richmond Hill"
    },
    {
      text: "Annual maintenance plan paid for itself the first year — they caught a cracked heat exchanger that would've been a CO problem next winter. I don't write reviews but I'm writing this one.",
      author: "Priya S.",
      location: "Thornhill"
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[var(--color-slate-blue)] to-blue-800 text-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
                Furnace acting up? AC quitting on a Tuesday? We can be there today.
              </h1>
              <p className="text-xl mb-8 text-gray-100">
                Northbridge HVAC has been keeping York Region homes comfortable since 2018. Licensed gas technicians, transparent pricing, and we don't leave a mess. Book a service call online or call (905) 555-0142.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center gap-2 bg-[var(--color-warm-amber)] text-white px-6 py-3 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
                >
                  Book Service Call
                  <ArrowRight size={20} />
                </Link>
                <a 
                  href="tel:9055550142" 
                  className="inline-flex items-center justify-center gap-2 bg-white text-[var(--color-slate-blue)] px-6 py-3 rounded text-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  <Phone size={20} />
                  (905) 555-0142
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1631545806609-2dceae6cd9b1?w=900&q=80&auto=format&fit=crop"
                alt="HVAC technician servicing a residential furnace"
                width={900}
                height={600}
                loading="eager"
                className="rounded-lg shadow-xl mb-6 w-full object-cover aspect-[3/2]"
              />
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4 text-white">Emergency Service Available</h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                    <span>No heat? We're there same-day</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                    <span>Licensed G2 technicians on every call</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                    <span>Upfront pricing before we start</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                    <span>Weekend and evening service</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Differentiators */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[var(--color-text-dark)]">
            Why York Region Homeowners Choose Us
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[var(--color-slate-blue)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Licensed gas technicians on every job
              </h3>
              <p className="text-gray-600">
                Every Northbridge technician holds a current TSSA G2 license. We don't sub out gas work to whoever's available — your furnace is touched by a Northbridge employee, not a contractor we met yesterday.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[var(--color-slate-blue)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Flat-rate pricing, written down before we start
              </h3>
              <p className="text-gray-600">
                You see the price before we touch a wrench. No "while we're in there" surprises, no after-the-fact line items. If a job changes scope, we stop and ask.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[var(--color-slate-blue)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                We protect the house
              </h3>
              <p className="text-gray-600">
                Drop sheets on the floor, shoe covers at the door, the workspace cleaner than we found it. Sounds basic. It's the most common complaint we hear from homeowners about the last HVAC company they used.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-[var(--color-text-dark)]">
            Our Services
          </h2>
          <p className="text-xl text-center mb-12 text-gray-600 max-w-3xl mx-auto">
            From emergency repairs to complete system replacements, we handle all your heating, cooling, and air quality needs.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <Link 
                  to={service.link} 
                  className="inline-flex items-center gap-2 text-[var(--color-slate-blue)] font-semibold hover:text-[var(--color-warm-amber)]"
                >
                  Learn more
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 bg-[var(--color-slate-blue)] text-white px-6 py-3 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[var(--color-text-dark)]">
            What Our Customers Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[var(--color-neutral-bg)] rounded-lg p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-[var(--color-warm-amber)] fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <strong className="text-[var(--color-text-dark)]">{testimonial.author}</strong>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-600">{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-[var(--color-text-dark)]">
            Serving York Region & North Toronto
          </h2>
          <p className="text-xl text-center mb-12 text-gray-600">
            Fast, reliable HVAC service across the Greater Toronto Area
          </p>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-text-dark)]">Our Service Areas</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Vaughan', 'Richmond Hill', 'Markham', 'Thornhill',
                  'Aurora', 'Newmarket', 'King City', 'Maple',
                  'Woodbridge', 'Kleinburg', 'North Toronto', 'Concord'
                ].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <MapPin size={16} className="text-[var(--color-slate-blue)]" />
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Link 
                  to="/service-area" 
                  className="inline-flex items-center gap-2 text-[var(--color-slate-blue)] font-semibold hover:text-[var(--color-warm-amber)]"
                >
                  View Full Service Area
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
              <div className="text-center p-8">
                <MapPin size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Service area map</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Ready to Book Your Service Call?
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Don't wait until your system fails. Schedule maintenance today or get a quote for your new installation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Book Online Now
              <ArrowRight size={20} />
            </Link>
            <a 
              href="tel:9055550142" 
              className="inline-flex items-center justify-center gap-2 bg-white text-[var(--color-slate-blue)] px-8 py-4 rounded text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Phone size={20} />
              Call (905) 555-0142
            </a>
          </div>
        </div>
      </section>
    </>
  );
}